import { isChecklistStatus } from '@cognite/apm-client';
import type {
  Checklist,
  ChecklistItem,
  CogniteSourceable,
  InFieldUser,
} from '@cognite/apm-client';

import {
  mapCdmMeasurementsToMeasurements,
  mapDmsMeasurementsToMeasurements,
} from '../../shared-utils/map-cdm-measurements-to-measurements';
import { sortOrderAscending } from '../../shared-utils/sort-order-ascending';

import { cdmChecklistServiceUtilsContext } from '../cdm-checklist-service-utils.context';
import type {
  CDMChecklistItemResponseType,
  CDMChecklistResponseType,
} from '../cdm-types';

type LegacyCreatableFields = {
  createdBy: Pick<InFieldUser, 'externalId'> | undefined;
  updatedBy: Pick<InFieldUser, 'externalId'> | undefined;
  lastUpdatedTime: number | undefined;
};

/** Back-fills legacy consumer fields from CogniteSourceable equivalents until callers migrate. */
const mapSourceableToLegacyCreatableFields = (
  item: CogniteSourceable
): LegacyCreatableFields => {
  const time = item.sourceUpdatedTime
    ? new Date(item.sourceUpdatedTime).getTime()
    : undefined;

  return {
    createdBy: item.sourceCreatedUser
      ? { externalId: item.sourceCreatedUser }
      : undefined,
    updatedBy: item.sourceUpdatedUser
      ? { externalId: item.sourceUpdatedUser }
      : undefined,
    lastUpdatedTime: time !== undefined && !isNaN(time) ? time : undefined,
  };
};

const getRelationItems = <T>(
  value: T[] | { items?: T[] } | null | undefined
): T[] | undefined => {
  if (value == null) {
    return undefined;
  }

  return Array.isArray(value) ? value : value.items;
};

type ChecklistMappingInput = CDMChecklistResponseType | Checklist;

type MapChecklistResponseOptions = {
  /** When status is present but not a known ChecklistStatus: skip the checklist (list) or omit status (detail). */
  onInvalidStatus?: 'skip' | 'omit';
};

/**
 * Internal sentinel from {@link resolveChecklistStatus} when list mapping should drop
 * a checklist. Not a `ChecklistStatus` and never exposed on the returned `Checklist`.
 */
const SKIP_CHECKLIST_ON_INVALID_STATUS = 'SKIP' as const;

const resolveChecklistStatus = (
  status: unknown,
  checklist: Pick<ChecklistMappingInput, 'externalId' | 'space'>,
  onInvalidStatus: MapChecklistResponseOptions['onInvalidStatus']
): Checklist['status'] | typeof SKIP_CHECKLIST_ON_INVALID_STATUS => {
  if (status == null || status === '') {
    return undefined;
  }

  if (isChecklistStatus(status)) {
    return status;
  }

  const operation =
    onInvalidStatus === 'skip'
      ? 'mapChecklistResponsesToChecklists'
      : 'normalizeChecklistFromDms';

  cdmChecklistServiceUtilsContext.captureMessage(
    onInvalidStatus === 'skip'
      ? 'Skipping checklist: status is not a known ChecklistStatus'
      : 'Checklist has an unrecognized status; omitting status field',
    {
      level: 'warning',
      tags: {
        component: 'cdm-checklist-service-utils',
        operation,
      },
      extra: {
        externalId: checklist.externalId,
        space: checklist.space,
        status,
      },
    }
  );

  return onInvalidStatus === 'skip'
    ? SKIP_CHECKLIST_ON_INVALID_STATUS
    : undefined;
};

type ChecklistItemMappingInput = CDMChecklistItemResponseType | ChecklistItem;

const getChecklistItemInputs = (
  checklist: ChecklistMappingInput
): ChecklistItemMappingInput[] | undefined => {
  const { checklistItems } = checklist;

  if (checklistItems == null) {
    return undefined;
  }

  if (Array.isArray(checklistItems)) {
    return checklistItems;
  }

  return checklistItems.items;
};

const getSolutionTagItems = (
  checklist: ChecklistMappingInput
): Checklist['solutionTags'] => {
  const { solutionTags } = checklist;

  if (solutionTags == null) {
    return undefined;
  }

  if (Array.isArray(solutionTags)) {
    return solutionTags;
  }

  return solutionTags.items;
};

const mapChecklistItemResponseToChecklistItem = (
  checklistItem: ChecklistItemMappingInput
): ChecklistItem => ({
  ...checklistItem,
  ...mapSourceableToLegacyCreatableFields(checklistItem), // TODO(FO-3295): Remove legacy Fields once consumers are migrated
  observations: (() => {
    if (checklistItem.observations == null) {
      return undefined;
    }

    const items = Array.isArray(checklistItem.observations)
      ? checklistItem.observations
      : checklistItem.observations.items;

    return items?.map((observation) => ({
      ...observation,
      files: getRelationItems(observation.files),
    }));
  })(),
  measurements: (() => {
    if (checklistItem.measurements == null) {
      return undefined;
    }

    // DMS uses flat Measurement[] (domain shape); GraphQL nests CDM MeasurementResponseType in { items }.
    if (Array.isArray(checklistItem.measurements)) {
      return mapDmsMeasurementsToMeasurements(checklistItem.measurements);
    }

    const items = checklistItem.measurements.items;
    return items ? mapCdmMeasurementsToMeasurements(items) : undefined;
  })(),
  files: getRelationItems(checklistItem.files),
});

const mapChecklistResponseToChecklist = (
  checklist: ChecklistMappingInput,
  { onInvalidStatus = 'skip' }: MapChecklistResponseOptions = {}
): Checklist | undefined => {
  const resolvedStatus = resolveChecklistStatus(
    checklist.status,
    checklist,
    onInvalidStatus
  );

  if (resolvedStatus === SKIP_CHECKLIST_ON_INVALID_STATUS) {
    return undefined;
  }

  const checklistItems = getChecklistItemInputs(checklist);

  return {
    ...checklist,
    status: resolvedStatus,
    ...mapSourceableToLegacyCreatableFields(checklist),
    solutionTags: getSolutionTagItems(checklist),
    checklistItems: checklistItems
      ?.map(mapChecklistItemResponseToChecklistItem)
      .sort(sortOrderAscending),
  };
};

/**
 * Maps checklist API responses (GraphQL or DMS) into the `Checklist` shape used
 * by InField hooks and UI.
 *
 * GraphQL returns nested relations as `{ items: [...] }` wrappers; DMS
 * `instances/query` returns the same relations as flat arrays. Both shapes are
 * normalized here, including measurements sorting and legacy creatable fields.
 *
 * Checklists whose `status` is missing or empty are kept. Checklists with an
 * unrecognized non-empty status are omitted from list results and reported to
 * Sentry — use {@link normalizeChecklistFromDms} for single-checklist fetches
 * where the status should be stripped instead.
 */
export const mapChecklistResponsesToChecklists = (
  items: CDMChecklistResponseType[]
): Checklist[] => {
  return items.flatMap((checklist) => {
    const mapped = mapChecklistResponseToChecklist(checklist);
    return mapped ? [mapped] : [];
  });
};

/**
 * Normalizes a checklist returned from `checklistsDMS.list` into the shape
 * expected by checklist UI hooks. DMS connects top-level relations as flat
 * arrays; GraphQL returns paginated `{ items: [...] }` wrappers — both are
 * handled by the shared checklist response mapper.
 *
 * Unlike {@link mapChecklistResponsesToChecklists}, an unrecognized status does
 * not drop the checklist: status is omitted so detail views can still render
 * items (DMS may return `""` or other values GraphQL never surfaced).
 */
export const normalizeChecklistFromDms = (
  checklist: ChecklistMappingInput
): Checklist => {
  const mapped = mapChecklistResponseToChecklist(checklist, {
    onInvalidStatus: 'omit',
  });

  if (mapped === undefined) {
    throw new Error('Checklist not found');
  }

  return mapped;
};
